import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { RotateCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { DateCell } from "@/components/DateCell";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

interface CreditMemo {
  id: string;
  creditMemoNumber: string;
  memoType: string;
  status: string;
  reason: string | null;
  totalAmount: string;
  memoDate: string;
  createdAt: string;
}

interface CreditMemosResponse {
  creditMemos: CreditMemo[];
  total: number;
}

export default function CreditMemosPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [memoTypeFilter, setMemoTypeFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading } = useQuery<CreditMemosResponse>({
    queryKey: ["/api/credit-memos", { search: searchValue, page: currentPage, limit: pageSize, status: statusFilter, memoType: memoTypeFilter }],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(pageSize),
      });
      if (searchValue) params.set("search", searchValue);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (memoTypeFilter !== "all") params.set("memoType", memoTypeFilter);
      return api.get<CreditMemosResponse>(`/credit-memos?${params}`, sessionId);
    },
  });

  const columns = [
    {
      key: "createdAt",
      header: "Entry",
      sortable: true,
      cell: (row: CreditMemo) => <DateCell value={row.createdAt} className="text-muted-foreground" />,
    },
    {
      key: "creditMemoNumber",
      header: "Memo #",
      sortable: true,
      cell: (row: CreditMemo) => (
        <div className="font-medium" data-testid={`text-credit-memo-number-${row.id}`}>{row.creditMemoNumber}</div>
      ),
    },
    {
      key: "memoType",
      header: "Type",
      cell: (row: CreditMemo) => (
        <Badge variant={row.memoType === "AR" ? "default" : "secondary"}>
          {row.memoType}
        </Badge>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (row: CreditMemo) => <StatusBadge status={row.status} type="invoice" />,
    },
    {
      key: "reason",
      header: "Reason",
      cell: (row: CreditMemo) => (
        <span className="text-muted-foreground truncate max-w-[200px] inline-block">{row.reason || "-"}</span>
      ),
    },
    {
      key: "totalAmount",
      header: "Total",
      className: "text-right",
      cell: (row: CreditMemo) => (
        <span className="font-medium">${Number(row.totalAmount || 0).toLocaleString()}</span>
      ),
    },
    {
      key: "memoDate",
      header: "Date",
      cell: (row: CreditMemo) => <DateCell value={row.memoDate} className="text-muted-foreground" />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Credit Memos"
        description="AR and AP credit memos for adjustments and returns"
        actions={
          <div className="flex items-center gap-2">
            <Select value={memoTypeFilter} onValueChange={(v) => { setMemoTypeFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="AR">AR</SelectItem>
                <SelectItem value="AP">AP</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="applied">Applied</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Search credit memos..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-64"
            />
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={data?.creditMemos || []}
        totalCount={data?.total || 0}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
        onRowClick={(row) => setLocation(`/credit-memos/${row.id}`)}
        emptyIcon={<RotateCcw className="h-12 w-12 text-muted-foreground/50" />}
        emptyMessage="No credit memos"
      />
    </div>
  );
}
